import { useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  FormControl,
  Grid,
  TextField,
  Typography,
} from "@mui/material";

import FileImportPrePicking from "components/FileImport/FileImportPrePicking";
import PrePickingReport from "components/PrePicking/PrePickingReport";
import PageLayout from "components/layout/PageLayout";
import { getScopedClientIds } from "helpers/clientScope";
import { useToast } from "hooks/useToast";
import { useSession } from "providers/SessionProvider";
import {
  PrePickingValidationResponse,
  PrePickingValidationShortageResponse,
} from "types";

export default function PrePickingPage() {
  const { clients, articles } = useSession();
  const { showSnackBar } = useToast();
  const [selectedClientId, setSelectedClientId] = useState<number | null>(null);
  const [validationResult, setValidationResult] =
    useState<PrePickingValidationResponse | null>(null);

  const activeClients = useMemo(
    () => clients.filter((client) => client.is_active && client.parent_client_id == null),
    [clients],
  );

  const selectedClient = useMemo(
    () =>
      activeClients.find((client) => client.id === selectedClientId) ?? null,
    [activeClients, selectedClientId],
  );

  const scopedClientIds = useMemo(
    () =>
      selectedClientId ? getScopedClientIds(clients, selectedClientId) : [],
    [clients, selectedClientId],
  );

  const clientArticles = useMemo(
    () =>
      articles.filter((article) => scopedClientIds.includes(article.client_id)),
    [articles, scopedClientIds],
  );

  const handleValidated = (response: PrePickingValidationResponse) => {
    setValidationResult(response);

    if (response.success) {
      showSnackBar(`${response.message}. ${response.detail}`, "success");
      return;
    }

    showSnackBar(response.message, "warning");
  };

  const shortageResult =
    validationResult && !validationResult.success && "report" in validationResult
      ? (validationResult as PrePickingValidationShortageResponse)
      : null;

  return (
    <PageLayout
      pageTitle="Pre-Picking"
      pageDesc="Validacion de stock disponible sin generar movimientos ni afectar inventario"
      importButton={
        <FileImportPrePicking
          clientId={selectedClientId}
          disabled={!selectedClientId}
          articles={clientArticles}
          onValidated={handleValidated}
        />
      }
    >
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth margin="none">
            <Autocomplete
              id="prepicking-client"
              autoComplete
              value={
                selectedClient
                  ? {
                      id: selectedClient.id,
                      label: `${selectedClient.code} - ${selectedClient.razon_social}`,
                    }
                  : null
              }
              options={activeClients.map((client) => ({
                id: client.id,
                label: `${client.code} - ${client.razon_social}`,
              }))}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={(_, value) => {
                setSelectedClientId(value?.id ?? null);
                setValidationResult(null);
              }}
              renderInput={(params) => (
                <TextField {...params} required label="Cliente" />
              )}
            />
          </FormControl>
        </Grid>

        {!selectedClientId && (
          <Grid item xs={12}>
            <Typography variant="body2" color="text.secondary">
              Seleccione un cliente para habilitar la importacion del Excel de pre-picking.
            </Typography>
          </Grid>
        )}

        {validationResult?.success && (
          <Grid item xs={12}>
            <Alert severity="success">
              <strong>{validationResult.message}</strong>
              <br />
              {validationResult.detail}
            </Alert>
          </Grid>
        )}

        {shortageResult && (
          <Grid item xs={12}>
            <PrePickingReport
              client={shortageResult.client}
              summary={shortageResult.summary}
              report={shortageResult.report}
            />
          </Grid>
        )}
      </Grid>
    </PageLayout>
  );
}
